import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { skills, hobbies } from '../portfolioitems';
import { useInView } from 'react-intersection-observer';
import Typist from 'react-typist';
import Textloop from 'react-text-loop';

function About({ windowHeight }) {
  const [skillRef, skillInView] = useInView();
  const [expRef, expInView] = useInView();
  const [aboutRef, aboutInView] = useInView();

  const mySkills = skills.map(({ skill, profeciency }, i) => {
    return (
      <motion.div
        key={i}
        className='flex items-center w-[80%] h-[25px] bg-blue-500 rounded-l-sm z-50 ml-20 md:ml-32'
        initial={{ opacity: 0 }}
        animate={{ opacity: skillInView ? 1 : 0 }}
        transition={{ delay: 0.3 }}
      >
        <p className='text-center text-sm font-bold w-[100px] min-w-[100px]'>
          {skill}
        </p>
        <div className='bg-gray-300 w-full'>
          <motion.div
            className='bg-blue-400 pt-[25px] rounded-r-sm'
            initial={{ width: 0 }}
            animate={{ width: profeciency + '%' }}
            transition={{ duration: 2, ease: 'easeInOut', delay: i * 0.5 }}
          ></motion.div>
        </div>
      </motion.div>
    );
  });

  const displayHobby = hobbies.map((hobby) => {
    return (
      <HobbiesDiv>
        {hobby.icon}
        <br />
        <p>{hobby.caption}</p>
      </HobbiesDiv>
    );
  });

  const aboutVariant = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.5,
        duration: 1,
      },
    },
  };

  const aboutChildVariant = {
    hidden: { opacity: 0, y: 100 },
    show: { opacity: 1, y: 0, transition: { duration: 1 } },
  };

  return (
    <Container height={windowHeight}>
      <div className='pt-40 md:pt-20 md:space-y-44 w-full grid'>
        <div className='text-5xl md:leading-snug place-self-center font-medium h-screen lg:text-6xl'>
          <Typist cursor={{ show: false }}>
            <h1>
              I'm a<br />
              <BlueText className='text-6xl lg:text-7xl'>
                Web Developer
              </BlueText>
              <br />
              {`&`}
              <br />
              <BlueText className='text-6xl lg:text-7xl'>
                Photoshop Wiz,
              </BlueText>
              <br />
              and I like making{' '}
              <PinkText className='text-7xl lg:text-8xl text-pink-500'>
                pretty
              </PinkText>{' '}
              things.
            </h1>
          </Typist>
        </div>
        {/*  */}
        <motion.div
          ref={aboutRef}
          className='about-div text-center text-lg font-light tracking-wide -mt-20 md:mt-0'
          variants={aboutVariant}
          hidden='hidden'
          animate={aboutInView ? 'show' : 'hidden'}
        >
          <motion.h1
            className='text-7xl font-bold tracking-wider pb-10'
            variants={aboutChildVariant}
          >
            About<span className='text-blue-150'>.</span>
          </motion.h1>
          <motion.p
            className='text-center max-w-screen-md place-self-center'
            variants={aboutChildVariant}
          >
            I enjoy turning
            <span className='italic'> complex problems </span>
            into something{' '}
            <motion.span
              initial={{ color: 'rgba(255, 255, 255, 1)' }}
              animate={{ color: 'rgba(236, 72, 153, 1)' }}
              transition={{ duration: 1, delay: 1 }}
            >
              <PinkText className='text-2xl'>beautiful</PinkText>
            </motion.span>
            . With my highly adaptable and learning driven personality, I seek
            ways to fix problems I encounter and grown to embrace the hardships
            that development can bring.
          </motion.p>
          <motion.h2 className='text-center' variants={aboutChildVariant}>
            <br />
            When I'm not pulling all my hair out writing code,
          </motion.h2>
          <motion.h4 className='text-center' variants={aboutChildVariant}>
            you can find me
          </motion.h4>
          <motion.div variants={aboutChildVariant}>
            <Textloop
              interval={2000}
              springConfig={{ stiffness: 100, damping: 10 }}
            >
              {displayHobby}
            </Textloop>
          </motion.div>

          <div className=' pt-52'></div>
        </motion.div>
        {/*  */}
        <div ref={skillRef} className='about-div max-w-screen-lg'>
          <motion.h1
            className='z-0 sticky top-20 -ml-20 place-self-start transform -rotate-90 text-green-400 font-bold text-8xl md:text-9xl'
            initial={{ opacity: 0 }}
            animate={{ opacity: skillInView ? 1 : 0 }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          >
            Skills
          </motion.h1>
          {mySkills}
        </div>
        <div
          ref={expRef}
          className='about-div md:grid-cols-2 mt-72 gap-y-5  max-w-screen-lg'
        >
          <motion.h1
            className='z-0 place-self-center text-red-400 font-bold text-7xl md:text-9xl col-span-full mb-20'
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: expInView ? 1 : 0, x: expInView ? 0 : 100 }}
            transition={{ delay: 0.3, duration: 1, ease: 'easeInOut' }}
          >
            Experience
          </motion.h1>

          <motion.div
            className='md:place-self-center ml-10 md:ml-0'
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: expInView ? 1 : 0, x: expInView ? 0 : 100 }}
            transition={{ delay: 1, duration: 1, ease: 'easeInOut' }}
          >
            <h1 className='font-medium text-4xl'>Software Engineer</h1>
            <div className='pl-4 pt-4'>
              <p className='pb-2 text-gray-500 text-sm'>
                MedHyve | Oct 2020 - Present
              </p>
              <p>Front-end Dev</p>
              <p>React.js</p>
              <p>Next.js</p>
            </div>
          </motion.div>
          <div className='hidden md:inline'></div>
          <div className='hidden md:inline'></div>
          <motion.div
            className='md:place-self-center ml-10 md:ml-0'
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: expInView ? 1 : 0, x: expInView ? 0 : -100 }}
            transition={{ delay: 1, duration: 1, ease: 'easeInOut' }}
          >
            <h1 className='font-medium text-4xl'>Freelancer</h1>
            <div className='pl-4 pt-4'>
              <p className='pb-2 text-gray-500 text-sm'>
                Upwork | 2019 - Present
              </p>
              <p>Photo manipulation</p>
              <p>Portrait & Product Retouch</p>
              <p>Composite, etc.</p>
            </div>
          </motion.div>
          <div className='hidden md:inline'></div>
          <div className='hidden md:inline'></div>

          <motion.div
            className='md:place-self-center ml-10 md:ml-0'
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: expInView ? 1 : 0, x: expInView ? 0 : 100 }}
            transition={{ delay: 1.3, duration: 1, ease: 'easeInOut' }}
          >
            <h1 className='font-medium text-4xl'>Computer Programmer</h1>
            <div className='pl-4 pt-4'>
              <p className='pb-2 text-gray-500 text-sm'>
                LUELCO | 2017 - Sept 2021
              </p>
              <p>Billing Software Maintenance</p>
              <p>Magic XPA 2.5 | VB.Net</p>
              <p>Website creation & maintenance</p>
            </div>
          </motion.div>
          <div className='hidden md:inline'></div>
          <div className='hidden md:inline'></div>
          <motion.div
            className='md:place-self-center ml-10 md:ml-0'
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: expInView ? 1 : 0, x: expInView ? 0 : -100 }}
            transition={{ delay: 1.6, duration: 1, ease: 'easeInOut' }}
          >
            <h1 className='font-medium text-4xl'>Clerk</h1>
            <div className='pl-4 pt-4'>
              <p className='pb-2 text-gray-500 text-sm'>LUELCO | 2016 - 2017</p>
              <p>Encoder</p>
              <p>Phone Operator</p>
              <p>Assist</p>
            </div>
          </motion.div>
        </div>
        <div className='pt-52'></div>
        {/* Link to resume */}
      </div>
    </Container>
  );
}

export default About;

const Container = styled.div`
  display: relative;
  height: ${(props) => props.height}px;
  width: 100vw;
  background-color: black;
  color: white;
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 20px;
`;

const BlueText = styled.span`
  font-weight: 700;
  --tw-text-opacity: 1;
  color: #0cf8d8;
`;

const PinkText = styled.span`
  font-family: 'Niconne', cursive;
`;

const ExpDiv = styled.div`
  place-self: center;
  width: 100%;
`;

const HobbiesDiv = styled.div`
  margin-top: 3rem;
  /* text-align: center; */
  width: 70vw;
  > .MuiSvgIcon-root {
    color: #0cf8d8;
    font-size: 150px;
  }
`;
